import {
  Competition,
  CompetitionPledge,
  CompetitionPledgeCategories,
  CompetitionScoreLevels,
  UserRead
} from '../userApi';
import { isDateEligibleForPledge, isUserTypeEligibleForPledge } from './userApiHelper';

export const maxPointsForPledge = (pledge: CompetitionPledge): number => {
  const repeatPoints = Math.min(pledge.points, 50);
  return pledge.points + repeatPoints * Math.max(0, pledge.repeats - 1);
};

export interface LevelBreakdownRow {
  scoreLevelName: string;
  threshold: number;
  actionCount: number;
  maxCompletions: number;
  maxPoints: number;
  cumulativeActions: number;
  cumulativeMaxPoints: number;
}

export interface SectionBreakdownRow {
  sectionName: string;
  actionCount: number;
  maxCompletions: number;
  maxPoints: number;
}

export interface SectionLevelMatrixRow {
  sectionName: string;
  levelCounts: number[];
}

export interface CatalogBreakdown {
  totalActions: number;
  totalMaxCompletions: number;
  totalMaxPoints: number;
  byLevel: LevelBreakdownRow[];
  bySection: SectionBreakdownRow[];
  sectionByLevel: SectionLevelMatrixRow[];
  scoreLevelNames: string[];
}

export const assignPledgeToLevelIndex = (
  pledge: CompetitionPledge,
  scoreLevels: CompetitionScoreLevels
): number => {
  if (!scoreLevels.length) {
    return 0;
  }
  for (let i = 1; i < scoreLevels.length; i++) {
    if (pledge.scoreLevelUUID === scoreLevels[i].uuid) {
      return i;
    }
  }
  return 0;
};

export const filterCatalogPledges = (
  pledges: CompetitionPledge[],
  competition: Competition | undefined,
  user: UserRead | undefined
): CompetitionPledge[] =>
  pledges.filter((pledge) => {
    if (pledge.active === false) {
      return false;
    }
    if (!isDateEligibleForPledge(pledge, competition)) {
      return false;
    }
    if (user && !isUserTypeEligibleForPledge(user, pledge)) {
      return false;
    }
    return true;
  });

export const computeCatalogBreakdown = (
  pledges: CompetitionPledge[],
  scoreLevels: CompetitionScoreLevels,
  categories: CompetitionPledgeCategories
): CatalogBreakdown => {
  const scoreLevelNames = scoreLevels.map((level) => level.name);
  const levelStats = scoreLevels.map((scoreLevel) => ({
    scoreLevelName: scoreLevel.name,
    threshold: scoreLevel.threshold || 0,
    actionCount: 0,
    maxCompletions: 0,
    maxPoints: 0
  }));

  const sectionStats = new Map<string, { actionCount: number; maxCompletions: number; maxPoints: number }>();
  const sectionLevelStats = new Map<string, number[]>();

  pledges.forEach((pledge) => {
    const levelIndex = assignPledgeToLevelIndex(pledge, scoreLevels);
    const maxPoints = maxPointsForPledge(pledge);
    levelStats[levelIndex].actionCount += 1;
    levelStats[levelIndex].maxCompletions += pledge.repeats;
    levelStats[levelIndex].maxPoints += maxPoints;

    const category = categories[pledge.competitionPledgeCategoryID];
    const sectionName = category ? category.name : '(Uncategorized)';
    const sectionEntry = sectionStats.get(sectionName) || {
      actionCount: 0,
      maxCompletions: 0,
      maxPoints: 0
    };
    sectionEntry.actionCount += 1;
    sectionEntry.maxCompletions += pledge.repeats;
    sectionEntry.maxPoints += maxPoints;
    sectionStats.set(sectionName, sectionEntry);

    const levelCounts = sectionLevelStats.get(sectionName) || scoreLevels.map(() => 0);
    levelCounts[levelIndex] += 1;
    sectionLevelStats.set(sectionName, levelCounts);
  });

  let cumulativeActions = 0;
  let cumulativeMaxPoints = 0;
  const byLevel = levelStats.map((row) => {
    cumulativeActions += row.actionCount;
    cumulativeMaxPoints += row.maxPoints;
    return {
      ...row,
      cumulativeActions,
      cumulativeMaxPoints
    };
  });

  const bySection = Array.from(sectionStats.entries())
    .map(([sectionName, stats]) => ({ sectionName, ...stats }))
    .sort((a, b) => b.maxPoints - a.maxPoints || a.sectionName.localeCompare(b.sectionName));

  const sectionByLevel = Array.from(sectionLevelStats.entries())
    .map(([sectionName, levelCounts]) => ({ sectionName, levelCounts }))
    .sort((a, b) => a.sectionName.localeCompare(b.sectionName));

  const totalMaxCompletions = pledges.reduce((sum, pledge) => sum + pledge.repeats, 0);
  const totalMaxPoints = pledges.reduce((sum, pledge) => sum + maxPointsForPledge(pledge), 0);

  return {
    totalActions: pledges.length,
    totalMaxCompletions,
    totalMaxPoints,
    byLevel,
    bySection,
    sectionByLevel,
    scoreLevelNames
  };
};
